import {
  IDrawingShapeData,
  IPresentationData,
  ISlideData,
  ISlideFileData,
  SlideApp,
  SlideFile,
} from '@tencent/slide-sdk-model';
import { cloneDeep, sample, shuffle } from 'lodash';
import { marked } from 'marked';
import { ISlideType, SlideImageType, SlideTagExtra, SlideType, LegacySlideType } from './define';
import { Token } from './define/token';
import { paginate } from './paginate';
import { createSchemaFromIdMap, getShapeTag, IDMap } from './schema';

import { removePrefix } from './utils/text-utils';
import { createPages, generateSlide, PageOptions, parsePage, parseTable } from './parse-page';

import preset from './resources/preset.json';
import { uuid } from './utils/utils';
import { matchLayoutIdByType } from './template-processor';
import { convertSlideType, getSlideTag, getSlideTagExtra } from './utils';

export interface ITemplate {
  id: string;
  type: SlideType;
  tag: SlideTagExtra;
  slideData: ISlideData;
  weight: number;

  /** 是否来自用户选择的模板 */
  fromSamePresentation: boolean;
}

const weightByImageType = {
  [SlideImageType.Page]: 10000,
  [SlideImageType.List]: 1000,
  [SlideImageType.None]: 100,
};

class Generator {
  static instance: Generator;
  static getInstance() {
    if (!Generator.instance) {
      Generator.instance = new Generator();
    }
    return Generator.instance;
  }

  private baseTemplateByType: Record<ISlideType, ITemplate[]> = createEmptyTypeMap();
  private userTemplateByType: Record<ISlideType, ITemplate[]> = createEmptyTypeMap();
  public templateByType: Record<ISlideType, ITemplate[]> = createEmptyTypeMap();

  public componentMap: Record<'title', IDrawingShapeData[]> = {
    title: [],
  };

  public layoutIdMappingByType: Record<ISlideType, string>;

  baseTemplate: IPresentationData;
  userTemplate: IPresentationData;

  catalogueTexts: string[] = [];

  context: {
    sessionCount: number;
    imageKeyArr: Array<ImageKey>;
    preTagMap?: PreTagMap;
  } = {
    sessionCount: 0,
    imageKeyArr: [],
  };

  updateTypeMap = () => {
    const typeMap = Object.keys(this.baseTemplateByType).reduce<Record<ISlideType, ITemplate[]>>((map, type) => {
      map[type] = [...this.userTemplateByType[type], ...this.baseTemplateByType[type]];
      return map;
    }, createEmptyTypeMap());
    this.templateByType = typeMap;
  };

  getCandidates = (templates: ITemplate[], count: number = 1): ITemplate[] => {
    const tpls = shuffle(templates);
    tpls.sort((a, b) => b.weight - a.weight);
    return tpls.slice(0, count);
  };

  updateTemplate = (template: IPresentationData, templateId: string) => {
    this.context.preTagMap = preTagAll[templateId];
    this.context.sessionCount = 0;

    this.userTemplateByType = createEmptyTypeMap();

    // const usedId = this.baseTemplate.slideList.map((slide) => slide.id);
    // TODO: 重复 id 处理

    template.slideList.forEach((slideData) => {
      // if (usedId.includes(slideData.id)) {
      //   console.info('冲突 id', slideData.id);
      //   slideData.id = uuid();
      //   console.info('new id', slideData.id);
      // }

      const tag = getSlideTagExtra(slideData) || getPreTag(slideData);
      if (!tag) return;

      const template: ITemplate = {
        id: `${uuid().slice(0, 4)}-${slideData.id}`,
        type: tag.type,
        tag,
        slideData,
        // 用户选中的模板，权重应增加
        weight: weightByImageType[tag.imageType] + 1,
        fromSamePresentation: true,
      };

      this.userTemplateByType[tag.type].push(template);
    });

    this.layoutIdMappingByType = matchLayoutIdByType(template.layoutMap);

    this.updateTypeMap();

    this.userTemplate = template;

    console.info(
      'userTemplateByType',
      Object.values(this.userTemplateByType).map((l) => l.length),
    );
  };

  updateBaseTemplate = (template: IPresentationData) => {
    if (!this.baseTemplate) {
      this.baseTemplate = template;
    } else {
      this.baseTemplate.slideList = [...this.baseTemplate.slideList, ...template.slideList];
    }

    let { slideList } = template;

    // resetTypeMap();

    slideList.forEach((slideData) => {
      const tag = getSlideTagExtra(slideData);
      if (!tag) return;

      if (tag.type === SlideType.component) {
        slideData.cSld.spTree?.shapeList.forEach((shape) => {
          const shapeTag = getShapeTag(shape);
          if (!shapeTag?.isTemplate) return;

          if (shapeTag.type === 'title') {
            this.componentMap.title.push(shape);
          }
        });
        return;
      }

      const template: ITemplate = {
        id: `${uuid().slice(0, 4)}-${slideData.id}`,
        type: tag.type,
        tag,
        slideData,
        weight: weightByImageType[tag.imageType],
        fromSamePresentation: false,
      };
      this.baseTemplateByType[tag.type].push(template);
    });

    this.updateTypeMap();

    console.info(
      'baseTemplateByType',
      Object.values(this.baseTemplateByType).map((l) => l.length),
    );
  };

  /** 生成全文 */
  generateSlideFromMarkdown = (markdownText: string, context?: ServerContext): IPresentationData => {
    let slideList: ISlideData[] = [];

    const OUTLINE_REGEXP = /^\[Outline\|(.*)\]\n?/;
    const execArray = OUTLINE_REGEXP.exec(markdownText);
    if (execArray) {
      const templateId = execArray[1];
      const outline = markdownText.replace(OUTLINE_REGEXP, '');
      const result = generateSlideListFromMarkdownPage(outline);
      const firstPage = result[0] ?? this.userTemplate.slideList[0];

      if (firstPage.cSld.spTree) {
        const tags = getSlideTag(firstPage) ?? {
          // TODO: for 元宝
          isTemplate: false,
          type: SlideType.cover,
        };

        tags.continueWritingContext = {
          outline,
          templateId,
          ...context,
        };

        firstPage.cSld.spTree.nvPr.descr = JSON.stringify(tags);
      }
      slideList = [firstPage];
    } else {
      slideList = generateSlideListFromMarkdownPage(markdownText);
    }

    this.userTemplate.slideList = slideList;
    return this.userTemplate;
  };
  /**
   * 生成单页
   * @param markdownText 单页的 markdown 文本
   * @param slideType 单页的类型
   * @param pageCount 输出候选个数; <=0时输出所有匹配模板。
   */
  generateSlideFromMarkdownPage = (
    markdownText: string,
    slideType?: SlideType,
    options: PageOptions = { pageCount: 1, hasChart: false },
  ): Result => {
    this.context.imageKeyArr = [];
    const slideList = generateSlideListFromMarkdownPage(markdownText, slideType, options);

    return {
      presentationData: {
        ...this.userTemplate,
        slideList,
      },
      metaInfo: {
        imageKeyArr: this.context.imageKeyArr,
      },
    };
  };

  /** 生成全文(序列化数据) */
  generateSlideDataFromMarkdown = (markdownText: string, context?: ServerContext): ISlideFileData => {
    const presentationData = this.generateSlideFromMarkdown(markdownText, context);

    const slideList = presentationData.slideList;
    presentationData.slideList = [];
    const app = SlideApp.fromPresentationData(presentationData);
    app.presentation.api.addSlides(slideList);

    return app.getSerializedPresentationData();
  };

  parseMarkdownTable = (markdownText: string): { title: string; tableData: string[][] } => {
    const tokens = marked.lexer(markdownText);
    const tableToken = tokens.find((tk) => tk.type === 'table') as marked.Tokens.Table;
    const headingToken = tokens.find((tk) => tk.type === 'heading') as marked.Tokens.Heading;
    return {
      title: headingToken.text,
      tableData: parseTable(tableToken),
    };
  };
}

export const generator = Generator.getInstance();

function createEmptyTypeMap(): Record<ISlideType, ITemplate[]> {
  return {
    [SlideType.cover]: [],
    [SlideType.catalogue]: [],
    [SlideType.session]: [],
    [SlideType.page]: [],
    [SlideType.ending]: [],
  };
}

export const convert = (slideData: ISlideFileData): IPresentationData => {
  const slideApp = SlideApp.fromCommandData(new SlideFile(slideData).getCommandData());
  return slideApp.presentation.getWritableOwnedData();
};

type PreTagMap = Record<string, { type: LegacySlideType; idMap: IDMap }>;
const preTagAll: Record</** templateId */ string, PreTagMap> = preset;

const getPreTag = (slideData: ISlideData): SlideTagExtra | undefined => {
  const temp = generator.context.preTagMap?.[slideData.id];
  if (temp) {
    return {
      type: convertSlideType(temp.type),
      idMap: temp.idMap,
      schema: createSchemaFromIdMap(temp.idMap),
      imageType: temp.idMap.image
        ? SlideImageType.Page
        : temp.idMap.list?.[0].image
          ? SlideImageType.List
          : SlideImageType.None,
    };
  }
};

const generateSlideListFromMarkdownPage = (
  markdownText: string,
  slideType?: SlideType,
  options: PageOptions = { pageCount: 1, hasChart: false },
): ISlideData[] => {
  const tokenList = marked.lexer(markdownText);
  if (slideType === SlideType.catalogue) {
    return createCatalogue(markdownText, options);
  }
  if (slideType === SlideType.ending) {
    return createEnding(options);
  }
  if (slideType === SlideType.session) {
    generator.context.sessionCount += 1;
  }

  return parsePage(tokenList, options);
};

const generateSlideListFromMarkdown = (markdownText: string): ISlideData[] => {
  const originTokens = marked.lexer(markdownText);
  // 暂时移除重整逻辑
  // const { tokens: tokenList } = refactorTree(originTokens);
  const tokenList = originTokens;

  // ## => ###
  const headTokens = tokenList.filter((token) => token.type === 'heading') as marked.Tokens.Heading[];

  if (headTokens.every((tk) => tk.depth <= 2)) {
    headTokens.forEach((tk) => {
      if (tk.depth === 2) {
        tk.depth = 3;
      }
    });
  }

  const pages = paginate(tokenList as Token[]);

  const slideList = pages.flatMap((page) => parsePage(page));

  // 目录
  const listText = createCatalogueList(tokenList);
  const catalogue = createCatalogue(listText);
  slideList.splice(1, 0, ...catalogue);

  // 尾页
  const [endingPage] = createEnding();
  slideList.push(endingPage);

  return slideList;
};

interface ServerContext {
  conversationId?: string;
  messageId?: string;
  enableImageGeneration?: boolean;
}

interface ImageKey {
  shapeId: string;
  keyword: string;
}

interface Result {
  presentationData: IPresentationData;
  metaInfo: {
    imageKeyArr: Array<ImageKey>;
  };
}

/** 检查 markdown 是否有三级标题 */
const hasThirdTitle = (tokens: Token[]) => {
  return tokens.some((token) => token.type === 'heading' && token.depth === 3);
};

export const createCatalogueList = (markdown: string | Token[]): string => {
  let tokens: Token[];
  if (typeof markdown === 'string') {
    tokens = marked.lexer(markdown);
  } else {
    tokens = markdown;
  }

  const sessionTitles: marked.Tokens.Heading[] = tokens.filter(
    (token): token is marked.Tokens.Heading => token.type === 'heading' && token.depth === 2,
  );

  const listText = sessionTitles
    .map((token) => {
      const text = removePrefix(token.text);
      return `- ${text}`;
    })
    .join('\n');

  return listText;
};

const createCatalogue = (listText: string, options?: PageOptions): ISlideData[] => {
  if (!listText) return [];

  const tokens = marked.lexer(listText);

  const [page] = createPages(tokens);
  return generateSlide(page, SlideType.catalogue, options);
};

const createEnding = (options?: PageOptions): ISlideData[] => {
  return generateSlide({}, SlideType.ending, options);
};
